// Proposal Pipeline 2.0 — an internal, staged, per-item pipeline that turns raw
// AI proposal intents into a validated "proposal package" with per-item status.
// It is PURE (no browser globals) so it is fully unit-testable, and it emits the
// EXISTING standard proposal kinds (create/edit/delete/move/...), so the current
// applyProposal() can execute every item unchanged.
//
// Stages: 0 Intent · 1 Conflict split · 2 Conflict classification ·
// 3 Coordinator · 4 Modification planner · 5 Event builder · 6 Triple validation ·
// 7 Strategy evaluation · 8 Guardian (per-item status) · 9 Final scan → package.
// A single bad item is isolated (status REJECTED/NEEDS_CLARIFICATION); the batch
// is never discarded.

import { CATEGORY_MAP } from "./categories";

const VALID_KINDS = new Set(["create", "edit", "delete", "move", "extend", "shorten", "mark"]);
const TASK_KINDS = new Set(["task_create", "task_edit", "task_delete", "task_complete", "task_skip"]);
const DESTRUCTIVE = new Set(["delete", "move", "split", "replace", "trim"]);
// Event kinds that MUST reference an existing event (by id).
const EXISTING_REF_KINDS = new Set(["edit", "delete", "move", "extend", "shorten", "mark"]);

export const STATUS = {
  AUTO: "AUTO_APPROVABLE",
  NORMAL: "NORMAL_APPROVAL",
  SEPARATE: "SEPARATE_APPROVAL",
  CLARIFY: "NEEDS_CLARIFICATION",
  REJECTED: "REJECTED",
  BLOCKED: "EXECUTION_BLOCKED",
};

const mins = (h, m) => (h || 0) * 60 + (m || 0);
const eStart = (e) => mins(e.startHour, e.startMinute);
const eEnd = (e) => mins(e.endHour, e.endMinute);

let _seq = 0;
function itemId(prefix = "it") {
  _seq += 1;
  return `${prefix}_${Date.now().toString(36)}_${_seq}`;
}

// Content key used to drop EXACT-duplicate proposals at ingest, so a plan that
// (e.g.) repeats the same nightly "Uni" create thousands of times is reviewed
// as ONE row with correct counts — instead of 1 valid + N-1 REJECTED duplicate
// rows that both render and inflate the day-group count. Distinct events differ
// in at least one identifying field, so they are preserved.
function dedupeKey(p) {
  const n = p.new || {};
  const o = p.old || {};
  return [
    p.kind || "",
    p.event_id || "",
    p.task_id || "",
    n.title || o.title || "",
    n.date || o.date || p._day || "",
    n.startHour ?? "", n.startMinute ?? "",
    n.endHour ?? "", n.endMinute ?? "",
    n.category || "",
  ].join("|");
}
function dedupeProposals(list) {
  const seen = new Set();
  const out = [];
  for (const p of list) {
    const k = dedupeKey(p);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(p);
  }
  return out;
}

// ---- Stage 0: Intent -------------------------------------------------------
export function readIntent(proposals) {
  const kinds = new Set(proposals.map((p) => p.kind));
  if (kinds.has("delete") && kinds.has("create")) return "replace";
  if (kinds.size === 1) {
    const k = [...kinds][0];
    if (k === "create") return "create";
    if (k === "delete") return "delete";
    if (k === "move") return "move";
    if (k === "edit" || k === "extend" || k === "shorten" || k === "mark") return "edit";
  }
  return "mixed";
}

// ---- Deterministic conflict DECISION layer ---------------------------------
// Structured, rule-based decision about whether/how a conflict may be resolved.
// PURE + unit-testable. The AI may EXPLAIN this outcome but must never override it.
const MANDATORY_PRIORITY = 5;
const IMPORTANT_PRIORITY = 4;
// Categories that typically involve other people (used only when the event has
// no explicit affectsOthers flag). Kept small and conservative.
const AFFECTS_OTHERS_CATEGORIES = new Set(["social_activity", "family_services"]);

export function isMandatory(e) {
  return e?.mandatory === true || (e?.priority ?? 3) >= MANDATORY_PRIORITY;
}
export function isMovable(e) {
  if (e?.movable === false) return false; // explicit lock
  return !isMandatory(e);
}
export function affectsOthers(e) {
  return e?.affectsOthers === true || AFFECTS_OTHERS_CATEGORIES.has(e?.category);
}

// evaluateConflict(newEvent, existingEvent) → structured decision.
// Considers: priority, mandatory status, movable status, affects-other-people,
// notes, intentional Part-of-Another-Event overlap, category and event ids.
export function evaluateConflict(newEvent, existingEvent) {
  const reasonCodes = [];
  const intentional =
    (newEvent?.parentEventId && newEvent.parentEventId === existingEvent?.id) ||
    (existingEvent?.parentEventId && existingEvent.parentEventId === newEvent?.id) || false;
  if (intentional) {
    reasonCodes.push("intentional_parent_child");
    return {
      intentional: true, severity: "none", canMoveExisting: true,
      requiresConfirmation: false, recommendedAction: "allow_overlap",
      reasonCodes, affectedIds: [existingEvent?.id].filter(Boolean),
    };
  }
  const mandatory = isMandatory(existingEvent);
  const movable = isMovable(existingEvent);
  const important = (existingEvent?.priority ?? 3) >= IMPORTANT_PRIORITY;
  const others = affectsOthers(existingEvent);
  const hasNotes = !!(existingEvent?.description && String(existingEvent.description).trim());

  if (mandatory) reasonCodes.push("existing_mandatory");
  if (!movable) reasonCodes.push("existing_not_movable");
  if (important) reasonCodes.push("existing_important");
  if (others) reasonCodes.push("existing_affects_others");
  if (hasNotes) reasonCodes.push("existing_has_notes");

  const canMoveExisting = movable && !mandatory;
  const requiresConfirmation = mandatory || !movable || important || others;
  const severity = mandatory || !movable ? "high" : important || others ? "medium" : "low";
  const recommendedAction = canMoveExisting
    ? (requiresConfirmation ? "confirm_before_adjusting_existing" : "adjust_existing")
    : "keep_existing_ask_user"; // a mandatory/non-movable event is never auto-adjusted
  return {
    intentional: false, severity, canMoveExisting, requiresConfirmation,
    recommendedAction, reasonCodes, affectedIds: [existingEvent?.id].filter(Boolean),
  };
}

// ---- BLACK conflict detection (invalid / blocked states) -------------------
// A BLACK conflict is an invalid or unsafe state that must NEVER be persisted,
// even with general user approval or in Founder mode. Deterministic + pure.
export function blackReasonsFor(item, existingEvents, tempTokens) {
  const codes = [];
  const n = (item && item.new) || {};
  const eventId = item && item.event_id;
  if (n.startHour != null && n.endHour != null) {
    const s = n.startHour * 60 + (n.startMinute || 0);
    const e = n.endHour * 60 + (n.endMinute || 0);
    if (e <= s && !n.crossMidnight) codes.push("invalid_time_range");
  }
  const pid = n.parentEventId;
  if (pid) {
    const byId = new Map((existingEvents || []).map((x) => [x.id, x]));
    if (eventId && pid === eventId) codes.push("self_parent");
    if (byId.has(pid)) {
      let cur = byId.get(pid);
      let guard = 0;
      while (cur && cur.parentEventId && guard++ < 25) {
        if (cur.parentEventId === eventId || cur.parentEventId === pid) {
          codes.push("circular_parent");
          break;
        }
        cur = byId.get(cur.parentEventId);
      }
    } else if (!(tempTokens && tempTokens.has(pid))) {
      // References a parent that is neither an existing event nor a same-batch
      // temp create -> missing / deleted parent.
      codes.push("missing_parent");
    }
  }
  return codes;
}

// ---- Stage 2: Conflict classification --------------------------------------
// Classify a NEW event against the existing events sharing its date.
// Returns { type, actions } where actions are standard proposals that adjust the
// EXISTING events to make room (the incoming create is added by the caller).
export function classifyConflict(newEvent, existingSameDay) {
  const ns = eStart(newEvent);
  const ne = eEnd(newEvent);
  const hits = existingSameDay.filter((x) => eStart(x) < ne && ns < eEnd(x));
  if (hits.length === 0) return { type: "NONE", actions: [] };
  if (hits.length > 1) {
    // Type D — one event overlaps multiple existing events.
    const actions = [];
    for (const x of hits) actions.push(...classifyConflict(newEvent, [x]).actions);
    return { type: "MULTIPLE", actions };
  }
  const x = hits[0];
  const xs = eStart(x);
  const xe = eEnd(x);

  // Type E — exact overlap → replace (remove existing, keep incoming).
  if (xs === ns && xe === ne) {
    return { type: "EXACT_REPLACE", actions: [mkDelete(x, "Replaced by the new event at the same time")] };
  }
  // New event fully covers existing → delete covered.
  if (ns <= xs && xe <= ne) {
    return { type: "COVERED_DELETE", actions: [mkDelete(x, "Fully covered by the new event")] };
  }
  // Type A — new event sits INSIDE existing → split existing into two.
  if (xs < ns && ne < xe) {
    return {
      type: "SPLIT",
      actions: [
        mkEdit(x, { endHour: newEvent.startHour, endMinute: newEvent.startMinute }, "Split: first part before the new event"),
        mkCreateFrom(x, { startHour: newEvent.endHour, startMinute: newEvent.endMinute }, "Split: continuation after the new event"),
      ],
    };
  }
  // Type B — overlap at the START of existing → trim existing start forward.
  if (ns <= xs && ne < xe) {
    return { type: "TRIM_START", actions: [mkEdit(x, { startHour: newEvent.endHour, startMinute: newEvent.endMinute }, "Trimmed start to fit the new event")] };
  }
  // Type C — overlap at the END of existing → trim existing end back.
  if (xs < ns && xe <= ne) {
    return { type: "TRIM_END", actions: [mkEdit(x, { endHour: newEvent.startHour, endMinute: newEvent.startMinute }, "Trimmed end to fit the new event")] };
  }
  return { type: "UNRESOLVED", actions: [] };
}

function mkDelete(x, reason) {
  return { kind: "delete", event_id: x.id, old: { title: x.title, date: x.date }, new: null, reason };
}
function mkEdit(x, patch, reason) {
  return { kind: "edit", event_id: x.id, old: { date: x.date, title: x.title, ...pick(x, Object.keys(patch)) }, new: patch, reason, _day: x.date };
}
function mkCreateFrom(x, patch, reason) {
  return {
    kind: "create",
    event_id: null,
    old: null,
    new: {
      title: x.title, date: x.date,
      startHour: x.startHour, startMinute: x.startMinute,
      endHour: x.endHour, endMinute: x.endMinute,
      category: x.category, priority: x.priority ?? 3, description: x.description || "", status: "planned",
      ...patch,
    },
    reason,
  };
}
function pick(obj, keys) {
  const o = {};
  for (const k of keys) o[k] = obj[k];
  return o;
}

// ---- Stage 6: Triple validation (per item) ---------------------------------
function validateTechnical(p) {
  if (!p || typeof p.kind !== "string") return "malformed proposal";
  if (!VALID_KINDS.has(p.kind) && !TASK_KINDS.has(p.kind)) return "unknown kind";
  if (p.kind === "create") {
    const n = p.new || {};
    if (!n.title || !String(n.title).trim()) return "missing title";
    if (typeof n.date !== "string" || Number.isNaN(new Date(`${n.date}T00:00:00`).getTime())) return "invalid date";
    if (!(eEnd(n) > eStart(n))) return "end must be after start";
  }
  return null;
}
function validateRules(p) {
  const n = p.new || {};
  if (p.kind === "create" && n.category && !CATEGORY_MAP[n.category]) return "invalid category";
  if (p.kind === "create" && n.priority != null && (n.priority < 1 || n.priority > 5)) return "invalid priority";
  return null;
}
function validateConsistency(p, existingById) {
  if (EXISTING_REF_KINDS.has(p.kind)) {
    // Must reference a real existing event (id resolved earlier by resolveEventRef).
    // A missing OR unknown reference is surfaced as NEEDS_CLARIFICATION so the
    // operation is never silently dropped nor silently applied to nothing.
    if (!p.event_id || (existingById && !existingById.has(p.event_id))) {
      return "references unknown event";
    }
  }
  return null;
}

// Resolve an existing-event operation to a real event id: prefer the id the AI
// gave; if that is missing or stale, fall back to matching the snapshot by
// title + date (+ start time to disambiguate). Single clearly-defined boundary
// so every executor path receives a reliable reference.
function resolveEventRef(p, byId, existingList) {
  if (p.event_id && byId.has(p.event_id)) return p.event_id;
  const ref = p.old || {};
  const alt = p.new || {};
  const title = String(ref.title || alt.title || "").trim().toLowerCase();
  const date = ref.date || alt.date || null;
  if (!title && !date) return p.event_id || null;
  const cand = existingList.filter(
    (e) => (!date || e.date === date) && (!title || String(e.title || "").trim().toLowerCase() === title)
  );
  if (cand.length === 1) return cand[0].id;
  if (cand.length > 1) {
    const sh = ref.startHour ?? alt.startHour;
    const sm = ref.startMinute ?? alt.startMinute;
    if (sh != null) {
      const narrowed = cand.filter((e) => e.startHour === sh && (e.startMinute || 0) === (sm || 0));
      if (narrowed.length === 1) return narrowed[0].id;
    }
  }
  return p.event_id || null;
}

// ---- Stage 8: Guardian (per item) ------------------------------------------
function guardianStatus(item) {
  if (!item.validation.ok) return item.validation.consistency ? STATUS.CLARIFY : STATUS.REJECTED;
  // BLACK conflict: invalid/unsafe state — execution is blocked entirely and can
  // never be approved (not even by Founder). Never downgraded to a normal overlap.
  if (item.black) return STATUS.BLOCKED;
  // RED conflict: a change touching a protected existing event always requires
  // explicit confirmation — never auto-approved.
  if (item.decision && item.decision.requiresConfirmation) return STATUS.SEPARATE;
  if (DESTRUCTIVE.has(item.kind) || item.conflictType === "EXACT_REPLACE" || item.conflictType === "SPLIT" || item.conflictType === "COVERED_DELETE") {
    return STATUS.SEPARATE;
  }
  if (item.kind === "create" && !item.conflictType && (item.new?.priority ?? 3) <= 2) return STATUS.AUTO;
  return STATUS.NORMAL;
}

// ---- Main: run the pipeline ------------------------------------------------
/**
 * runPipeline(proposals, existingEvents) → package
 * proposals: array of standard AI proposals (create/edit/delete/move/...).
 * Returns a package with per-item status; NEVER throws; isolates bad items.
 */
export function runPipeline(proposals, existingEvents = []) {
  const list = dedupeProposals(Array.isArray(proposals) ? proposals.filter(Boolean) : []);
  const intent = readIntent(list); // Stage 0

  // Existing events indexed by date + by id (id set is grown as we plan creates
  // so later edits referencing just-created items validate correctly).
  const byDate = new Map();
  for (const e of existingEvents) {
    if (!byDate.has(e.date)) byDate.set(e.date, []);
    byDate.get(e.date).push(e);
  }
  const existingIds = new Set(existingEvents.map((e) => e.id).filter(Boolean));
  const byId = new Map(existingEvents.filter((e) => e.id).map((e) => [e.id, e]));

  // Same-batch temporary parent tokens (parent creates carry new.tempId; a child
  // create references it via new.parentEventId). Such refs are valid — the
  // executor links them after the parent is created — so they are NOT "missing".
  const tempTokens = new Set(list.map((p) => p && p.new && p.new.tempId).filter(Boolean));

  // Existing events the AI is ALREADY modifying in this same batch
  // (edit/delete/move/extend/shorten). The AI has already made room for its
  // creates around these, so the pipeline must NOT auto-resolve the same
  // conflict a second time — doing so double-trims/splits the event, producing
  // conflicting edits + duplicate continuation events (changes then apply
  // incorrectly). Auto-resolution still runs for any existing event the AI did
  // NOT touch.
  const SPACE_CHANGING = new Set(["edit", "delete", "move", "extend", "shorten"]);
  const handledIds = new Set(
    list.filter((p) => SPACE_CHANGING.has(p.kind) && p.event_id).map((p) => p.event_id)
  );

  const items = [];

  const addItem = (proposal, extra = {}) => {
    const technical = validateTechnical(proposal);
    const rules = technical ? null : validateRules(proposal);
    const consistency = technical || rules ? null : validateConsistency(proposal, existingIds);
    const validation = { technical, rules, consistency, ok: !technical && !rules && !consistency };
    const day = (proposal.new && proposal.new.date) || (proposal.old && proposal.old.date) || proposal._day || null;
    const inferred = (proposal.new && Array.isArray(proposal.new.inferred) && proposal.new.inferred)
      || (Array.isArray(proposal.inferred) && proposal.inferred)
      || (extra.conflictType && extra.conflictType !== "NONE" ? ["conflict"] : null);
    const item = {
      id: itemId(),
      proposalId: null, // stable unique id, set below
      kind: proposal.kind,
      event_id: proposal.event_id ?? null,
      new: proposal.new ?? null,
      old: proposal.old ?? null,
      reason: proposal.reason || extra.reason || "",
      task_id: proposal.task_id ?? null,
      day,
      conflictType: extra.conflictType || null,
      decision: extra.decision || null,
      inferred: inferred || null,
      validation,
      status: STATUS.NORMAL,
    };
    item.proposalId = item.id;
    // Deterministic BLACK detection (invalid/unsafe) + severity classification.
    const blackCodes = [
      ...blackReasonsFor(item, existingEvents, tempTokens),
      ...((extra.blackExtra) || []),
    ];
    item.black = blackCodes.length > 0;
    item.blackReasons = item.black ? blackCodes : null;
    item.severity = item.black
      ? "black"
      : item.decision && item.decision.requiresConfirmation
      ? "red"
      : item.conflictType && item.conflictType !== "NONE"
      ? "informational"
      : "none";
    item.status = guardianStatus(item); // Stage 7/8 combined per-item
    items.push(item);
    return item;
  };

  for (const p of list) {
    // Stage 1 (split) + Stage 2 (classify) + Stage 4 (plan) for creates.
    if (p.kind === "create" && p.new && p.new.date) {
      // A child create is intentionally inside its parent — never auto-resolve
      // that as a conflict/split against the referenced parent event.
      const parentRef = p.new.parentEventId || null;
      const sameDay = (byDate.get(p.new.date) || []).filter((x) => !handledIds.has(x.id) && x.id !== parentRef);
      const { type, actions } = classifyConflict(p.new, sameDay);
      // adjustments to existing events first (so they read naturally, grouped by day)
      const newLocked = isMandatory(p.new) && !isMovable(p.new);
      let mandatoryCollision = false;
      for (const a of actions) {
        let decision = null;
        if (a.event_id) {
          const ex = byId.get(a.event_id);
          if (ex) {
            decision = evaluateConflict(p.new, ex);
            // Two locked (mandatory + non-movable) events that must overlap have
            // no safe deterministic resolution -> BLACK on the create.
            if (newLocked && isMandatory(ex) && !isMovable(ex)) mandatoryCollision = true;
          }
        }
        addItem(a, { conflictType: type, decision });
      }
      addItem(p, {
        conflictType: type === "NONE" ? null : type,
        blackExtra: mandatoryCollision ? ["mandatory_collision"] : null,
      });
    } else {
      // Resolve existing-event operations (edit/delete/move/extend/shorten/mark)
      // to a reliable event id (id-first, then title+date+time fallback) and
      // carry the resolved event's date so the item groups under the right day.
      if (EXISTING_REF_KINDS.has(p.kind)) {
        const rid = resolveEventRef(p, byId, existingEvents);
        const ev = rid ? byId.get(rid) : null;
        addItem({
          ...p,
          event_id: rid,
          _day: (ev && ev.date) || (p.new && p.new.date) || (p.old && p.old.date) || null,
        });
      } else {
        addItem(p);
      }
    }
  }

  // Stage 9 — final scan: duplicates, impossible times, missing dates, new conflicts.
  const finalScan = { duplicates: 0, impossibleTimes: 0, missingDates: 0 };
  const seen = new Set();
  for (const it of items) {
    if (it.kind === "create" && it.new) {
      const key = `${it.new.title}|${it.new.date}|${it.new.startHour}:${it.new.startMinute}`;
      if (seen.has(key)) { finalScan.duplicates += 1; it.status = STATUS.REJECTED; it.reason = it.reason || "duplicate"; }
      else seen.add(key);
      if (!(eEnd(it.new) > eStart(it.new))) { finalScan.impossibleTimes += 1; it.status = STATUS.REJECTED; }
      if (!it.new.date) { finalScan.missingDates += 1; it.status = STATUS.REJECTED; }
    }
  }

  const summary = summarize(items);
  const groupsByDate = groupByDate(items);

  return {
    packageId: itemId("pkg"),
    intent,
    items,
    summary,
    groupsByDate,
    finalScan,
    createdAt: new Date().toISOString(),
  };
}

function summarize(items) {
  const s = { creates: 0, edits: 0, deletes: 0, moves: 0, splits: 0, tasks: 0, autoApprovable: 0, needsClarification: 0, rejected: 0, blocked: 0, separate: 0, total: items.length, actionable: 0 };
  for (const it of items) {
    if (it.status === STATUS.REJECTED) { s.rejected += 1; continue; }
    // BLACK/BLOCKED items can NEVER be applied (excluded by approvableItems and
    // by the executor's authoritative gate), so they must not count as actionable.
    if (it.status === STATUS.BLOCKED) { s.blocked += 1; continue; }
    // NEEDS_CLARIFICATION items are NOT approvable (excluded by approvableItems),
    // so they must not inflate the "actionable" count shown in Proposal Review.
    if (it.status === STATUS.CLARIFY) { s.needsClarification += 1; continue; }
    if (it.status === STATUS.AUTO) s.autoApprovable += 1;
    if (it.status === STATUS.SEPARATE) s.separate += 1;
    s.actionable += 1;
    if (it.conflictType === "SPLIT" && it.kind === "edit") s.splits += 1;
    if (it.kind === "create") s.creates += 1;
    else if (it.kind === "edit" || it.kind === "extend" || it.kind === "shorten" || it.kind === "mark") s.edits += 1;
    else if (it.kind === "delete") s.deletes += 1;
    else if (it.kind === "move") s.moves += 1;
    else if (String(it.kind).startsWith("task_")) s.tasks += 1;
  }
  return s;
}

function groupByDate(items) {
  const map = new Map();
  for (const it of items) {
    const d = it.day || "—";
    if (!map.has(d)) map.set(d, []);
    map.get(d).push(it);
  }
  return [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, its]) => ({ date, items: its }));
}

// Convert a package item back into a plain proposal object for applyProposal().
// Strips the internal "inferred" marker so it is never persisted on the event.
export function itemToProposal(item) {
  let newEvent = item.new;
  if (newEvent && newEvent.inferred) {
    newEvent = { ...newEvent };
    delete newEvent.inferred;
  }
  return {
    kind: item.kind,
    event_id: item.event_id,
    task_id: item.task_id,
    old: item.old,
    new: newEvent,
    reason: item.reason,
  };
}
